import { useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { Card, CardHeader } from '../../../components/ui/Card';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { DataNotes, InfoTip } from '../../../components/ui/InfoTip';
import { useSalesMeetings } from '../../../hooks/useProjectManagement';
import { RepBadge } from '../components/RepBadge';
import { SALES_REPS, getRep } from '../team';
import type { SalesRepSlug } from '../team';
import { plural } from '../formatters';
import { formatDayMonthTime, formatTime } from '../../../utils/dateFormat';
import { dayLabel } from './meetingsWeek';
import { isKnownNonProspect } from './nonProspects';
import { useCsmRoster } from '../../../hooks/useDashboard';
import type { AeMeeting } from '../../../api/projectManagement';
import { SectionToolbar } from '../../Sales/SectionToolbar';
import { AttioLink, bareDomain, companyLabel } from '../../Sales/shared';
import { attioCompanyUrl } from '../../Dashboard/csm/attio';
import type { MeetingsPeriod } from '../../Sales/meetings/period';

/** Meetings → prospect meetings: the AEs' calendar meetings with prospects in the page's period. */
export function ProspectMeetings({
  leading,
  period,
  periodControl,
}: {
  leading?: ReactNode;
  period: MeetingsPeriod;
  periodControl?: ReactNode;
}) {
  const [filterRep, setFilterRep] = useState<SalesRepSlug | null>(null);
  const { data, error, refetch } = useSalesMeetings({ from: period.from, to: period.to });

  // An untyped meeting with only known non-prospects (our banks, a vendor) is not counted,
  // even from an API that still counts it (the API applies the same rule after deploy).
  // Roster customers count too (domain match); without the roster the rule falls back to the fixed list.
  const roster = useCsmRoster(true);
  const customerDomains = useMemo(
    () => new Set((roster.data?.rows ?? []).filter((r) => r.companyType === 'Customer' && r.lifecycleStage !== 'Churned').map((r) => bareDomain(r.domain)).filter(Boolean)),
    [roster.data],
  );
  const all = useMemo(() => (data?.meetings ?? []).filter((m) => !isKnownNonProspect(m, customerDomains)), [data, customerDomains]);
  const notCounted = useMemo(() => data?.notCounted ?? [], [data]);
  const [showUntyped, setShowUntyped] = useState(false);
  // Untyped companies that never booked: not counted (the API's rule since 14 Oct 2026). An older API still
  // returned them among the counted meetings, so both lists are read.
  const untypedCompanies = useMemo(() => {
    const seen = new Map<string, { label: string; domain: string; recordId: string | null }>();
    for (const m of [...all, ...notCounted]) {
      if (m.audience !== 'untyped') continue;
      for (const c of m.companies) seen.set(c.domain, { label: companyLabel(c.name, c.domain), domain: c.domain, recordId: c.recordId ?? null });
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [all, notCounted]);
  const repCounts = useMemo(() => countByRep(all), [all]);
  const heldCount = all.filter((m) => m.held).length;
  // A meeting two reps sit in counts for each of them, so the rep chips can add up to more than All.
  const sharedCount = all.filter((m) => m.reps.length > 1).length;
  const repTotal = Object.values(repCounts).reduce((a, b) => a + b, 0);
  const visible = useMemo(() => (filterRep ? all.filter((m) => m.reps.includes(filterRep)) : all), [all, filterRep]);
  // Newest day first over a long window; one week reads best in calendar order.
  const ordered = useMemo(() => (period.id === 'week' ? visible : [...visible].reverse()), [visible, period.id]);
  const { pageItems, ...paging } = usePagination(ordered);
  const byDay = useMemo(() => groupByDay(pageItems), [pageItems]);

  return (
    <div className="space-y-4">
      <SectionToolbar
        leading={leading}
        meta={
          <>
            {data?.lastSyncedAt ? `From Attio · synced ${formatDayMonthTime(data.lastSyncedAt)}` : 'From Attio'}
            {data && data.warnings.length > 0 && <DataNotes notes={data.warnings} />}
          </>
        }
        info={
          <>
            From the AEs&rsquo; calendars in Attio. A meeting counts when at least one outside company is a Prospect, Lead,
            Opportunity or ExCustomer in Attio, or has no type but booked a meeting with us: a Fillout demo form or a
            rep&rsquo;s Calendly page, matched by company or by the person&rsquo;s exact email. Meetings with customers,
            partners, suppliers and investors, and with untyped companies that never booked, are listed apart and not
            counted. A supplier is a company typed Supplier in Attio; a few vendors with no Attio company yet
            (Outboundie) and SEQUESTO's own banks (KBC, Revolut) are treated as suppliers by rule. Companies are matched to Attio by email domain, the same way
            Demo bookings does.
          </>
        }
        actions={periodControl}
      />

      <Card padding="md">
        {/* No count and no rep chips until the data has arrived: a loading page never reads "0". */}
        <CardHeader
          title={data ? `${plural(all.length, 'prospect meeting')} · ${period.label}` : `Prospect meetings · ${period.label}`}
          // Only when some are still to come: "44 held so far" under "44 prospect meetings" said the number twice.
          description={data && heldCount < all.length ? `${heldCount} held · ${all.length - heldCount} upcoming` : undefined}
          action={
            data && <div className="flex flex-wrap items-center justify-end gap-1.5">
              {sharedCount > 0 && (
                <span className="text-body-xs text-text-tertiary">
                  Per rep: meetings attended (shared meetings count for each rep)
                </span>
              )}
              <div className="inline-flex items-center gap-0.5 rounded border border-border-light bg-surface-secondary p-0.5" role="radiogroup" aria-label="Rep">
                <FilterChip count={all.length} active={filterRep === null} onClick={() => setFilterRep(null)}>
                  All
                </FilterChip>
                {SALES_REPS.map((r) => (
                  <FilterChip
                    key={r.slug}
                    count={repCounts[r.slug] ?? 0}
                    active={filterRep === r.slug}
                    onClick={() => setFilterRep(filterRep === r.slug ? null : r.slug)}
                  >
                    <RepBadge rep={r} size="sm" />
                  </FilterChip>
                ))}
              </div>
              {sharedCount > 0 && (
                <InfoTip label="Why the rep totals add up to more than All">
                  {plural(sharedCount, 'meeting')} had more than one rep in it, so the rep chips add up to {repTotal}{' '}
                  while All counts {all.length}: {all.length} + {repTotal - all.length} extra seat
                  {repTotal - all.length === 1 ? '' : 's'} for the shared meetings.
                </InfoTip>
              )}
            </div>
          }
        />

        {untypedCompanies.length > 0 && (
          <div className="mb-3 text-body-xs text-text-tertiary">
            <button
              type="button"
              onClick={() => setShowUntyped((v) => !v)}
              aria-expanded={showUntyped}
              className="inline-flex items-center gap-1 hover:text-text-primary"
            >
              <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', showUntyped && 'rotate-90')} aria-hidden />
              {plural(untypedCompanies.length, 'company', 'companies')} with no type in Attio and no booking
            </button>
            <span>
              {' '}· not counted; type a real prospect as Prospect in Attio to count its meetings
            </span>
            {showUntyped && (
              <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {untypedCompanies.map((c) => (
                  <li key={c.domain}>
                    {c.recordId ? (
                      <a href={attioCompanyUrl(c.recordId)} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                        {c.label}
                      </a>
                    ) : (
                      <span className="text-text-secondary" title={`No Attio company has the domain ${c.domain}`}>
                        {c.label} (not in Attio)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {error && !data ? (
          <ErrorState title="Couldn’t load the meetings" error={error} onRetry={() => refetch()} />
        ) : !data ? (
          <LoadingState rows={5} />
        ) : visible.length === 0 ? (
          <EmptyState
            compact
            title={filterRep ? `No prospect meetings for ${getRep(filterRep).name} ${period.label}` : `No prospect meetings ${period.label}`}
          />
        ) : (
          <>
            <ul className="space-y-3">
              {Object.entries(byDay).map(([day, meetings]) => (
                <li key={day} className="space-y-1.5">
                  <p className="text-body-xs text-text-tertiary">{day}</p>
                  <ul className="space-y-1">
                    {meetings.map((m) => <MeetingRow key={m.id} meeting={m} />)}
                  </ul>
                </li>
              ))}
            </ul>
            <Pagination {...paging} onPage={paging.setPage} />
          </>
        )}

        {data && notCounted.length > 0 && <NotCountedList meetings={notCounted} />}
      </Card>
    </div>
  );
}

function NotCountedList({ meetings }: { meetings: AeMeeting[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border-t border-border-light pt-3">
      <button type="button" className="inline-flex items-center gap-1 text-body-xs text-text-tertiary hover:text-text-primary" onClick={() => setOpen(!open)}>
        <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
        Not counted: {plural(meetings.length, 'meeting')} with customers, partners, suppliers, investors or untyped companies that never booked
      </button>
      {open && (
        <ul className="mt-2 space-y-1">
          {meetings.map((m) => <MeetingRow key={m.id} meeting={m} />)}
        </ul>
      )}
    </div>
  );
}

function FilterChip({ children, count, active, onClick }: { children: ReactNode; count: number; active: boolean; onClick: () => void }) {
  return (
    // Styled as the kit SegmentedControl, which takes text labels only; these carry a rep badge.
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-body-xs font-medium transition-colors',
        active ? 'bg-surface-primary text-text-primary shadow-subtle' : 'text-text-tertiary hover:text-text-secondary',
      )}
    >
      {children}
      <span className="tabular-nums">{count}</span>
    </button>
  );
}

function domainOf(email: string): string {
  return email.split('@')[1] ?? email;
}

/** "Supplier", "no type", "not in Attio": what the meeting's company is, the same words Demo bookings uses. */
function companyNote(c: AeMeeting['companies'][number]): string {
  if (c.typeFrom === 'supplier_rule') return c.name ? 'Supplier by rule' : 'Supplier by rule, not in Attio';
  if (c.type) return c.type;
  return c.name ? 'no type' : 'not in Attio';
}

/** Why an untyped meeting counts: say it once on the row. */
function bookedNote(meeting: AeMeeting): string {
  return meeting.audience === 'booked' ? ' · booked a meeting with us' : '';
}

function MeetingRow({ meeting }: { meeting: AeMeeting }) {
  const start = new Date(meeting.startTime);
  const time = meeting.endTime ? `${formatTime(start)}–${formatTime(new Date(meeting.endTime))}` : formatTime(start);
  const primary = meeting.external[0];
  const companies = meeting.companies.length > 0
    ? meeting.companies.map((c) => `${companyLabel(c.name, c.domain)} (${companyNote(c)})`)
    : [...new Set(meeting.external.map((p) => companyLabel(null, domainOf(p.email))))];
  const linked = meeting.companies.find((c) => c.recordId);

  return (
    <li className="flex items-start gap-3 py-2">
      <span className="w-24 flex-none text-body-xs text-text-tertiary tabular-nums pt-0.5">{time}</span>
      <span className="flex w-14 flex-none items-center gap-1 pt-0.5">
        {meeting.reps.map((r) => <RepBadge key={r} rep={getRep(r)} size="sm" />)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-body-sm font-medium text-text-primary truncate">{meeting.title || '(no title)'}</p>
        <p className="text-body-xs text-text-tertiary truncate">
          {primary ? (primary.name ? `${primary.name} · ${primary.email}` : primary.email) : ''}
          {meeting.external.length > 1 && ` +${meeting.external.length - 1} more`}
          {companies.length > 0 && ` · ${companies.join(', ')}`}
          {bookedNote(meeting)}
        </p>
      </div>
      {!meeting.held && <span className="text-body-xs text-text-tertiary shrink-0 pt-0.5">upcoming</span>}
      <span className="w-7 shrink-0">
        {linked?.recordId && <AttioLink href={attioCompanyUrl(linked.recordId)} label={linked.name ?? 'company'} />}
      </span>
    </li>
  );
}

function groupByDay(meetings: AeMeeting[]): Record<string, AeMeeting[]> {
  const map: Record<string, AeMeeting[]> = {};
  for (const m of meetings) {
    const key = dayLabel(new Date(m.startTime));
    (map[key] ??= []).push(m);
  }
  return map;
}

function countByRep(meetings: AeMeeting[]): Record<SalesRepSlug, number> {
  const result: Record<SalesRepSlug, number> = { patrick: 0, andrew: 0, nadia: 0 };
  for (const m of meetings) for (const r of m.reps) result[r]++;
  return result;
}
